// SimilarResource.js
// New component based on SimilarImage.js, but uses resource_id from URL to search similar resources via API
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import TimeAgo from "react-timeago";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { MEDIA_URL } from "../../configs";
import AuthLayout from "../../components/AuthLayout/AuthLayout";
import { setMessage, setSuccessMessage } from "../../redux/actions/message";
import format from "../../helpers/format";
import axios from "axios";
import Pagination from "../../components/Pagination";

dayjs.extend(relativeTime);

const defaultFilter = {
  resource_id: "",
  query: "",
  page: 1,
  page_size: 48,
  vector_type: "clip",
  fusion: "",
  ef_search: 128,
  top_k: 100,
  threshold: 200,
  // Add more keys here if you want to support more filters in the future
};

function SimilarResource(props) {
  const dispatch = useDispatch();
  const { message, messageType } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(defaultFilter);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [pageSize, setPageSize] = useState(48);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [originalResource, setOriginalResource] = useState(null);
  const [useColor, setUseColor] = useState(false);
  // advanced params
  const [fusionOption, setFusionOption] = useState("");
  const [efSearch, setEfSearch] = useState(128);
  const [topK, setTopK] = useState(100);
  const [thresholdVal, setThresholdVal] = useState(200);

  useEffect(() => {
    const newQuery = { ...defaultFilter };
    Object.keys(defaultFilter).forEach((key) => {
      if (searchParams.get(key)) {
        newQuery[key] = searchParams.get(key);
      }
    });
    // Always ensure page is set
    if (searchParams.get("page")) {
      newQuery.page = Number(searchParams.get("page"));
      setCurrentPage(Number(searchParams.get("page")));
    } else {
      newQuery.page = 1;
      setCurrentPage(1);
    }
    setQuery(newQuery);
    setUseColor(newQuery.vector_type === 'color');
  // initialize advanced params from URL
  setFusionOption(searchParams.get('fusion') || newQuery.fusion || '');
  setEfSearch(Number(searchParams.get('ef_search') || newQuery.ef_search || 128));
  setTopK(Number(searchParams.get('top_k') || newQuery.top_k || 100));
  setThresholdVal(Number(searchParams.get('threshold') || newQuery.threshold || 200));
    
    // Set search input based on what's in the URL
    if (searchParams.get("resource_id")) {
      setSearchInput(searchParams.get("resource_id"));
    } else if (searchParams.get("query")) {
      setSearchInput(searchParams.get("query"));
    }
  }, [searchParams]);

  // Sync currentPage with URL param
  useEffect(() => {
    const pageParam = Number(searchParams.get('page')) || 1;
    if (pageParam !== currentPage) {
      setCurrentPage(pageParam);
    }
  }, [searchParams]);

  // Fetch original resource when resource_id changes
  useEffect(() => {
    if (query.resource_id) {
      const fetchOriginal = async () => {
        try {
          const response = await axios.get(`http://127.0.0.1:8005/resource/${query.resource_id}`);
          setOriginalResource(response.data);
        } catch (error) {
          console.error("Error fetching original resource:", error);
          setOriginalResource(null);
        }
      };
      fetchOriginal();
    } else {
      setOriginalResource(null);
    }
  }, [query.resource_id]);

  useEffect(() => {
    if (!query.resource_id && !query.query) {
      // No search criteria provided
      return;
    }
    const fetchSimilarResources = async () => {
      setIsLoading(true);
      try {
        let response;
        
        // Check if searching by resource_id or keyword
        if (query.resource_id) {
          // Search by resource ID (similarity search)
            const baseParams = {
              resource_id: query.resource_id,
              page: query.page,
              page_size: query.page_size,
              // include tuning params when present
              top_k: query.top_k || topK,
              threshold: query.threshold || thresholdVal,
            };
            // include fusion/ef_search when set (for color/fusion searches)
            if (query.fusion || fusionOption) {
              baseParams.fusion = query.fusion || fusionOption;
            }
            if (query.ef_search || efSearch) {
              baseParams.ef_search = query.ef_search || efSearch;
            }
          if (query.vector_type === 'clip') {
            const params = new URLSearchParams({
              ...baseParams,
              vector_type: query.vector_type
            }).toString();
            response = await axios.get(`http://127.0.0.1:8005/search-similar?${params}`);
          } else {
            const params = new URLSearchParams(baseParams).toString();
            response = await axios.get(`http://127.0.0.1:8005/search-similar-color?${params}`);
          }
        }
        
        // Expecting { items, total, more_page }
        if (response.data && Array.isArray(response.data.items)) {
          setSearchResults(response.data.items);
          setTotal(response.data.total || 0);
          const searchType = query.resource_id ? "similar resources" : "resources matching your keyword";
          dispatch(setSuccessMessage(`Found ${response.data.total} ${searchType}`, "success"));
        } else {
          setSearchResults([]);
          setTotal(0);
          dispatch(setMessage("No results found", "info"));
        }
      } catch (error) {
        dispatch(setMessage(error.message, "danger"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchSimilarResources();
    // eslint-disable-next-line
  }, [query, dispatch]);

  const handlePageChange = (page) => {
    // Preserve either resource_id or query from searchParams
    const rid = query.resource_id || searchParams.get('resource_id');
    const keyword = query.query || searchParams.get('query');
    const newQuery = { ...query, page };
    if (rid) {
      newQuery.resource_id = rid;
      delete newQuery.query;
    } else if (keyword) {
      newQuery.query = keyword;
      delete newQuery.resource_id;
    }
    // preserve advanced params in URL
    if (fusionOption) newQuery.fusion = fusionOption;
    if (efSearch) newQuery.ef_search = efSearch;
    if (topK) newQuery.top_k = topK;
    if (thresholdVal !== undefined) newQuery.threshold = thresholdVal;
    setSearchParams(newQuery);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchInput.trim()) {
      dispatch(setMessage("Please enter a resource ID or keyword", "warning"));
      return;
    }

    // Determine if input is a resource ID (MongoDB ObjectId format) or keyword
    const isResourceId = /^[0-9a-fA-F]{24}$/.test(searchInput.trim());
    
    const newQuery = {
      page: 1,
      page_size: 48,
      vector_type: useColor ? 'color' : 'clip',
      fusion: fusionOption,
      ef_search: efSearch,
      top_k: topK,
      threshold: thresholdVal,
    };

    if (isResourceId) {
      newQuery.resource_id = searchInput.trim();
    // } else {
    //   newQuery.query = searchInput.trim();
    }

    setSearchParams(newQuery);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchResults([]);
    setTotal(0);
    setOriginalResource(null);
    setSearchParams({});
  };

  return (
    <AuthLayout>
      <header className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fs-5">Search Resources</h2>
        <Link className="btn btn-outline-secondary" to="/resources">
          Back to Resources
        </Link>
      </header>

      {/* Search Form */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
          <form onSubmit={handleSearch}>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                id="searchInput"
                placeholder="Enter Resource ID (24-character hex) or keyword to search..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button className="btn btn-primary" type="submit">
                Search
              </button>
            </div>
            <small className="form-text text-muted mt-1">
              Enter a 24-character resource ID for similar image search, or keywords for semantic search
            </small>
            <div className="form-check mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="useColorCheck"
                checked={useColor}
                onChange={(e) => setUseColor(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="useColorCheck">
                Use color similarity (for resource ID searches)
              </label>
            </div>
              {/* Advanced tuning controls for fusion, ef_search, top_k, threshold */}
              <div className="mt-3">
                <div className="row gx-2">
                  {useColor && (
                    <div className="col-auto">
                      <label className="form-label small">Fusion</label>
                      <select className="form-select form-select-sm" value={fusionOption} onChange={e => setFusionOption(e.target.value)}>
                        <option value="">(none)</option>
                        <option value="RRF">RRF</option>
                        <option value="AVERAGE">AVERAGE</option>
                        <option value="SUM">SUM</option>
                      </select>
                    </div>
                  )}
                  <div className="col-auto">
                    <label className="form-label small">ef_search</label>
                    <input type="number" className="form-control form-control-sm" value={efSearch} min={16} max={512} onChange={e => setEfSearch(Number(e.target.value))} />
                  </div>
                  <div className="col-auto">
                    <label className="form-label small">top_k</label>
                    <input type="number" className="form-control form-control-sm" value={topK} min={1} max={1000} onChange={e => setTopK(Number(e.target.value))} />
                  </div>
                  <div className="col-auto">
                    <label className="form-label small">threshold</label>
                    <input type="number" className="form-control form-control-sm" value={thresholdVal} onChange={e => setThresholdVal(Number(e.target.value))} />
                  </div>
                </div>
              </div>
          </form>
          {(query.resource_id || query.query) && !originalResource && (
            <div className="mt-3">
              <button className="btn btn-sm btn-outline-secondary" onClick={handleClearSearch}>
                Clear Search
              </button>
              <span className="ms-3 text-muted">
                {query.resource_id ? (
                  <>Searching similar to: <strong>{query.resource_id}</strong></>
                ) : (
                  <>Searching keyword: <strong>{query.query}</strong></>
                )}
              </span>
            </div>
          )}
          </div>
          {originalResource && (
            <div className="mt-3">
              <span className="me-2 text-muted">Searching similar to:</span>
              <div className="d-flex align-items-center">
                {originalResource.thumbnail && (
                  <img
                    src={`${MEDIA_URL}${originalResource.thumbnail.path}`}
                    alt={originalResource.name}
                    style={{ width: '140px', height: '140px', objectFit: 'cover' }}
                    className="me-2 rounded"
                  />
                )}
                
              </div>
              <p>{originalResource.name}</p>
            </div>
          )}
        </div>
      </div>

      {message && (
        <div className="mb-4">
          <div className={`alert alert-${messageType}`} role="alert">
            {message}
          </div>
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-12">
          {!query.resource_id && !query.query ? (
            <div className="text-center py-5">
              <p className="text-muted">
                Enter a resource ID or keyword above to start searching
              </p>
            </div>
          ) : isLoading ? (
            <div className="text-center py-5">
              <span className="spinner-border spinner-border-lg" />
              <p>Loading results...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <>
              <div className="row border-top-0 gx-2">
                {searchResults.map((resource, index) => (
                  <div
                    key={index}
                    className={`col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2 mb-4 rgrid-item align-middle`}
                  >
                    <div className="position-relative">
                      {resource?.thumbnail_path ? (
                        <LazyLoadImage
                          alt={resource.name}
                          src={`${MEDIA_URL}${resource.thumbnail_path}`}
                          height={160}
                          effect="blur"
                          className="img-preview"
                          onError={e => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.className = 'bg-light text-center py-5';
                            fallback.innerText = 'No Image';
                            e.target.parentNode.appendChild(fallback);
                          }}
                        />
                      ) : (
                        <div className="bg-light text-center py-5">No Image</div>
                      )}
                      <div className="position-absolute top-0 end-0 m-2">
                        <span className="badge bg-primary">
                          {((resource.similarity_score ?? resource.similarity) * 100).toFixed(1)}%
                        </span>
                      </div>
                      <Link to={`/resources/view/${resource._id ?? resource.resource_id}`}>
                        <p className="my-1"><strong className="rname small">
                          {resource.name}
                        </strong></p>
                      </Link>
                      {resource.created_at && (
                        <div className="small text-muted mb-2">
                          <TimeAgo date={resource.created_at} />
                        </div>
                      )}
                      {resource.tags && resource.tags.length > 0 && (
                        <div className="mb-2">
                          {resource.tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className="badge fw-normal bg-light text-dark me-1"
                            >
                              {typeof tag === 'string' ? tag : tag.name}
                            </span>
                          ))}
                          {resource.tags.length > 3 && (
                            <span className="badge fw-normal bg-light text-dark">
                              +{resource.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                {/* Pagination component, same as ResourceGrid.js */}
                
                <Pagination
                  currentPage={currentPage}
                  pageSize={query.page_size}
                  count={total}
                  onPageChange={handlePageChange}
                  path=""
                  query={query}
                />
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">
                No results found for your search.
              </p>
            </div>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}

function mapStateToProps(state) {
  const { token } = state.auth;
  const { message, messageType } = state.message;
  return {
    token,
    message,
    messageType,
  };
}

export default connect(mapStateToProps)(SimilarResource);
